/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { StyleSheet, Switch, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { BranchPermission, ModuleKey } from '../../../shell/session/store/sessionTypes';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import {
  permissionLevelFromTriple,
  type PermissionLevel,
} from '../../store/useEmployeesStore';
import { MODULE_LABELS_ES } from './employeePermissions/permissionUiLabels';
import { levelsForModule } from './employeePermissions/permissionLevelOptions';
import { setModuleLevelInPermissions } from './employeePermissions/permissionPreview';

type Props = {
  expanded: boolean;
  onToggleExpanded: () => void;
  visibleModules: ModuleKey[];
  permissions: BranchPermission[];
  onChange: (next: BranchPermission[]) => void;
  customized: boolean;
};

export function InvitePermissionCustomizer({
  expanded,
  onToggleExpanded,
  visibleModules,
  permissions,
  onChange,
  customized,
}: Props) {
  const [openModule, setOpenModule] = useState<string | null>(null);

  const permByModule = new Map(permissions.map((p) => [p.module, p]));
  const ordered = visibleModules
    .map((m) => permByModule.get(m))
    .filter((p): p is BranchPermission => p != null);

  const handleSelectLevel = (module: ModuleKey, level: PermissionLevel) => {
    onChange(setModuleLevelInPermissions(permissions, module, level));
  };

  const toggleAgendaField = (
    module: ModuleKey,
    field: 'canManageAgenda' | 'canViewOthers'
  ) => {
    onChange(
      permissions.map((p) => {
        if (p.module !== module) return p;
        if (field === 'canManageAgenda') {
          return { ...p, canManageAgenda: !p.canManageAgenda };
        }
        return { ...p, canViewOthers: p.canViewOthers === false };
      })
    );
  };

  return (
    <View style={styles.wrapper} testID="perfil.team.invite.customize-permissions">
      <TouchableOpacity
        style={styles.header}
        onPress={onToggleExpanded}
        accessibilityRole="button"
        accessibilityLabel="Personalizar permisos opcional"
        accessibilityState={{ expanded }}
      >
        <View style={styles.headerCopy}>
          <Text style={styles.headerTitle}>Personalizar permisos (opcional)</Text>
          <Text style={styles.headerHint}>
            {customized
              ? 'Ajustaste el rol. Podés cambiarlo de nuevo cuando acepte.'
              : 'Solo si el rol no te alcanza.'}
          </Text>
        </View>
        <View style={styles.headerRight}>
          {customized ? (
            <View style={styles.customBadge}>
              <Text style={styles.customBadgeText}>Personalizado</Text>
            </View>
          ) : null}
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={18}
            color={ticketColors.ink3}
          />
        </View>
      </TouchableOpacity>

      {expanded ? (
        <View style={styles.body}>
          {ordered.map((perm, idx) => {
            const moduleLabel = MODULE_LABELS_ES[perm.module] ?? perm.module;
            const currentLevel = permissionLevelFromTriple({
              canView: perm.canView,
              canEdit: perm.canEdit,
              canVoid: perm.canVoid ?? false,
              canDiscount: perm.canDiscount ?? false,
              canManageAgenda: perm.canManageAgenda ?? false,
            });
            const options = levelsForModule(perm.module);
            const currentOpt = options.find((o) => o.level === currentLevel) ?? options[0];
            const moduleExpanded = openModule === perm.module;
            const isLast = idx === ordered.length - 1;

            return (
              <View key={perm.module} style={[styles.moduleBlock, !isLast && styles.moduleBorder]}>
                <TouchableOpacity
                  style={styles.moduleTrigger}
                  onPress={() => setOpenModule(moduleExpanded ? null : perm.module)}
                  accessibilityRole="button"
                  accessibilityLabel={`${moduleLabel}: ${currentOpt.label}`}
                  accessibilityState={{ expanded: moduleExpanded }}
                >
                  <Text style={styles.moduleName}>{moduleLabel}</Text>
                  <View style={styles.moduleValue}>
                    <Text
                      style={[
                        styles.moduleValueText,
                        currentLevel !== 'none' && styles.moduleValueTextActive,
                      ]}
                    >
                      {currentOpt.shortLabel}
                    </Text>
                    <Ionicons
                      name={moduleExpanded ? 'chevron-up' : 'chevron-down'}
                      size={14}
                      color={ticketColors.ink3}
                    />
                  </View>
                </TouchableOpacity>

                {moduleExpanded ? (
                  <View style={styles.levelList}>
                    {options.map((opt) => {
                      const selected = opt.level === currentLevel;
                      return (
                        <TouchableOpacity
                          key={opt.level}
                          style={[styles.levelRow, selected && styles.levelRowActive]}
                          onPress={() => handleSelectLevel(perm.module, opt.level)}
                          accessibilityRole="button"
                          accessibilityLabel={opt.label}
                          accessibilityState={{ selected }}
                        >
                          <Text
                            style={[styles.levelRowText, selected && styles.levelRowTextActive]}
                          >
                            {opt.label}
                          </Text>
                          {selected ? (
                            <Ionicons name="checkmark" size={16} color={ticketColors.gold} />
                          ) : null}
                        </TouchableOpacity>
                      );
                    })}

                    {perm.module === 'Agenda' && currentLevel !== 'none' ? (
                      <>
                        <View style={styles.agendaRow}>
                          <View style={styles.agendaCopy}>
                            <Text style={styles.agendaTitle}>Administrar Agenda</Text>
                            <Text style={styles.agendaHint}>Horarios, ausencias y recursos.</Text>
                          </View>
                          <Switch
                            value={perm.canManageAgenda === true}
                            onValueChange={() => toggleAgendaField('Agenda', 'canManageAgenda')}
                            trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                            thumbColor={ticketColors.paper}
                            accessibilityLabel="Administrar Agenda"
                          />
                        </View>
                        <View style={styles.agendaRow}>
                          <View style={styles.agendaCopy}>
                            <Text style={styles.agendaTitle}>Ver agendas de otros</Text>
                            <Text style={styles.agendaHint}>
                              Si está apagado, solo ve turnos de su recurso.
                            </Text>
                          </View>
                          <Switch
                            value={perm.canViewOthers !== false}
                            onValueChange={() => toggleAgendaField('Agenda', 'canViewOthers')}
                            trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                            thumbColor={ticketColors.paper}
                            accessibilityLabel="Ver agendas de otros"
                          />
                        </View>
                      </>
                    ) : null}
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginTop: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.border,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
    padding: spacing.md,
  },
  headerCopy: {
    flex: 1,
    gap: spacing.xs,
  },
  headerTitle: {
    ...typography.body,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  headerHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingTop: 2,
  },
  customBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: ticketColors.gold + '25',
  },
  customBadgeText: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  body: {
    borderTopWidth: 1,
    borderTopColor: ticketColors.border,
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
  },
  moduleBlock: {
    paddingVertical: spacing.xs,
  },
  moduleBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.border,
  },
  moduleTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  moduleName: {
    ...typography.body,
    color: ticketColors.ink,
    flex: 1,
  },
  moduleValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  moduleValueText: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  moduleValueTextActive: {
    color: ticketColors.ink,
    fontWeight: '600',
  },
  levelList: {
    paddingHorizontal: spacing.xs,
    paddingBottom: spacing.sm,
    gap: 2,
  },
  levelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
  },
  levelRowActive: {
    backgroundColor: ticketColors.gold + '25',
  },
  levelRowText: {
    ...typography.body,
    color: ticketColors.ink2,
  },
  levelRowTextActive: {
    color: ticketColors.ink,
    fontWeight: '600',
  },
  agendaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    marginTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.border,
  },
  agendaCopy: {
    flex: 1,
    gap: 2,
  },
  agendaTitle: {
    ...typography.caption,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  agendaHint: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
});
